import {
    NoDataContainer,
    TextAlign,
    BooleanSearchContainer,
} from "pro-features/components/Rule/src/RuleModes/EditRule/EditRuleModeStyles";
import type { FC } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { SWReactIcons } from "@similarweb/icons";
import noop from "lodash/noop";
import { BooleanSearchInputWrap, BooleanSearchInput } from "@similarweb/ui-components";
import { ScrollArea } from "@similarweb/ui-components";
import { DotsLoader } from "@similarweb/ui-components";
import { i18nFilter } from "app/filters/i18nFilter";
import { Checkbox } from "@similarweb/ui-components";
import { RadioButton } from "@similarweb/ui-components";
import type { IFoldersFilter, IRule } from "app/pages/segments/redesigned-wizard/segment-wizard/types";
import { useSegmentWizardContext } from "app/pages/segments/redesigned-wizard/segment-wizard/SegmentWizardContextProvider";

import {
    CheckBoxContainer,
    BooleanSearchWrapStyled,
    FooterButton,
    DEFAULT_CHIPS_CONTAINER_HEIGHT,
} from "app/pages/segments/redesigned-wizard/segment-wizard/segment-rules-section/segmentRulesStyles";
import { RuleSelectedValuesRow } from "app/pages/segments/redesigned-wizard/segment-wizard/segment-rules-section/RuleSelectedValuesRow";
import { SegmentRulesScrollAreaContainer } from "app/pages/segments/redesigned-wizard/segment-wizard/styledComponents";
import { addItems, removeItems } from "app/pages/segments/redesigned-wizard/segment-wizard/segmentWizardHelpers";

const MAX_SHOWN_FOLDERS = 300;

export const FoldersFilter: FC<IFoldersFilter> = (props) => {
    const { folderPredictions, folderPredictionsLoading } = useSegmentWizardContext();
    const [currentRule, setCurrentRule] = useState<IRule>(null);

    /**
     * Filter out words when editing a rule. (filter out word prediction chips)
     */
    const [searchFoldersFilterValue, setSearchFoldersFilterValue] = useState<string>("");
    const inputPlaceholder = i18nFilter()("workspaces.segment.wizard.input_search.folders.placeholder");
    const { rule, isMidTierUser, onApply } = props;
    useEffect(() => {
        const componentRule = { ...rule };
        // Spread is a shallow copy, and I want to fully copy only the folders,
        // as the rest of the rule filters will remain unchanged while editing the current rule
        componentRule.folders = [...rule.folders];
        setCurrentRule(componentRule);
    }, []);

    const onAddFolders = useCallback(
        (folders: string[]) => {
            const updatedRule = addItems({ folders }, currentRule);
            setCurrentRule(updatedRule);
        },
        [currentRule],
    );

    const onRemoveFolders = useCallback(
        (folders: string[]) => {
            const updatedRule = removeItems("folders", folders, currentRule);
            setCurrentRule(updatedRule);
        },
        [currentRule],
    );
    const onApplyFilter = useCallback(
        () => onApply(currentRule, props.filterType, props.selectedTab),
        [onApply, currentRule, props.filterType, props.selectedTab],
    );

    const onFilterChange = (event) => {
        setSearchFoldersFilterValue(event?.currentTarget?.value?.trim());
    };
    const filteredFolderPredictions = useMemo(() => {
        const filtered = searchFoldersFilterValue
            ? folderPredictions?.Data.filter((val) => {
                  return val.toLowerCase().indexOf(searchFoldersFilterValue.toLowerCase()) !== -1;
              })
            : folderPredictions?.Data;
        return filtered?.slice(0, MAX_SHOWN_FOLDERS);
    }, [folderPredictions, searchFoldersFilterValue]);
    const OnCheckBoxToggle = (value) => (): void => {
        currentRule.folders.includes(value) ? onRemoveFolders([value]) : onAddFolders([value]);
    };

    const onRadioClick = (value) => (): void => {
        if (currentRule.folders.length > 0) {
            currentRule.folders.pop();
        }
        onAddFolders([value]);
    };

    const renderCheckboxItems = () => {
        return filteredFolderPredictions?.map((value) => {
            return (
                <CheckBoxContainer key={value}>
                    {isMidTierUser ? (
                        <RadioButton
                            itemLabel={value}
                            itemSelected={currentRule?.folders.includes(value)}
                            className={"folders"}
                            onClick={onRadioClick(value)}
                            key={value}
                        />
                    ) : (
                        <Checkbox
                            key={value}
                            className="folders"
                            selected={currentRule?.folders.includes(value)}
                            label={value}
                            onClick={OnCheckBoxToggle(value)}
                        />
                    )}
                </CheckBoxContainer>
            );
        });
    };

    const renderNoSearchData = () => {
        return (
            <NoDataContainer>
                <TextAlign>{i18nFilter()("segmentWizard.editRule.folders.no.results.part1")}</TextAlign>
                <TextAlign>{i18nFilter()("segmentWizard.editRule.folders.no.results.part2")}</TextAlign>
            </NoDataContainer>
        );
    };

    return (
        <>
            {currentRule?.folders?.length > 0 && (
                <RuleSelectedValuesRow
                    valuesList={currentRule.folders}
                    title={i18nFilter()("segmentWizard.editRule.folders.row.title")}
                    onRemoveValues={onRemoveFolders}
                    isFilterMode={true}
                />
            )}
            <BooleanSearchContainer>
                <BooleanSearchWrapStyled showBorderBottom={true}>
                    <BooleanSearchInputWrap>
                        <SWReactIcons
                            size="sm"
                            iconName={"search"}
                        />
                        <BooleanSearchInput
                            value={searchFoldersFilterValue}
                            onKeyUp={noop}
                            onChange={onFilterChange}
                            placeholder={inputPlaceholder}
                            autoFocus={true}
                        />
                        {folderPredictionsLoading && <DotsLoader />}
                    </BooleanSearchInputWrap>
                </BooleanSearchWrapStyled>
            </BooleanSearchContainer>
            {searchFoldersFilterValue && filteredFolderPredictions.length === 0 ? (
                renderNoSearchData()
            ) : (
                <SegmentRulesScrollAreaContainer>
                    <ScrollArea
                        style={{
                            height: DEFAULT_CHIPS_CONTAINER_HEIGHT,
                            zIndex: 99,
                            marginLeft: "19px",
                        }}
                        verticalScrollbarStyle={{ borderRadius: 8, width: 5 }}
                        verticalContainerStyle={{ width: 5 }}
                        minScrollSize={48}
                    >
                        {renderCheckboxItems()}
                    </ScrollArea>
                    <FooterButton
                        onClick={onApplyFilter}
                        isEnabled={currentRule?.folders?.length > 0}
                    >
                        {i18nFilter()("common.apply")}
                    </FooterButton>
                </SegmentRulesScrollAreaContainer>
            )}
        </>
    );
};
